import { PropsWithChildren, createContext, useCallback, useRef } from "react";

type ClaimOptions = {
  style?: string;
};

export type SharedMapContextType = {
  /**
   * Hands back a mapboxgl.Map-shaped object backed by a pooled, reused instance instead of a
   * fresh one - claiming reuses an already-created, currently-free instance when one exists
   * (reparenting its DOM container into `container`) and only calls `new mapboxgl.Map()` when
   * none are free. Consumers use the result exactly like a real map (.on/.off/.setStyle/
   * .fitBounds/etc.), with one difference: calling .remove() releases it back to the pool
   * (resetting it to a clean slate) instead of destroying it - the same call a consumer
   * already makes to tear a map down, so nothing about that teardown code has to change.
   *
   * Scope/limitations:
   * - Camera position (center/zoom) isn't part of the release-time reset - restoring it is
   *   app-specific behavior, not leftover content this pool can clean up generically.
   * - A released instance stays parked (not destroyed) for the rest of the session - there's
   *   no eviction.
   */
  claimMap: (container: HTMLDivElement, options?: ClaimOptions) => Promise<mapboxgl.Map>;
};

export const SharedMapContext = createContext<SharedMapContextType | undefined>(undefined);

type ListenerRecord = { type: string; listener: (...args: any[]) => void };

/** Every interaction handler a consumer can disable via mapboxgl.Map#<handler>.disable(). */
const INTERACTION_HANDLERS = [
  "scrollZoom",
  "boxZoom",
  "dragPan",
  "dragRotate",
  "keyboard",
  "doubleClickZoom",
  "touchZoomRotate",
  "touchPitch",
] as const satisfies readonly (keyof mapboxgl.Map)[];

type LeaseEntry = {
  map: mapboxgl.Map;
  /** undefined = free and available to be claimed. */
  owner: symbol | undefined;
  currentStyle: string | undefined;
  /** True once this instance's style has finished loading at least once. */
  hasLoaded: boolean;
  /** Every listener registered through the pooled map's .on() since the last release. */
  listeners: ListenerRecord[];
  /** Layer/source ids added through the pooled map's .addLayer()/.addSource() since the last release. */
  addedLayerIds: Set<string>;
  addedSourceIds: Set<string>;
  /** Set while a just-released entry's reset is still settling; resolves once it's free. */
  resettling?: Promise<void>;
  /**
   * Set while a claim's own setStyle (see tryClaimFreeEntry) has genuinely swapped to a
   * different style and that style hasn't finished loading yet. isStyleLoaded() isn't a
   * substitute - it can read false for unrelated reasons (e.g. in-flight tile loads) that
   * never fire "style.load" to resolve.
   */
  styleReloading: boolean;
  /**
   * Set when a consumer mutates something the fast path below can't revert by simply
   * removing it - a property on a layer/source that was already part of the base style
   * (e.g. toggling a POI layer's visibility), or a style config property. The next
   * release then falls back to a full style reload instead of the fast path.
   */
  needsFullReset: boolean;
};

/**
 * Strips an entry back to a clean slate. The fast path removes exactly the layers/
 * sources a consumer added (tracked via createPooledMap's addLayer/addSource
 * interception) instead of reloading the whole style to diff them away. If a consumer
 * mutated something that fast path can't revert (entry.needsFullReset), this falls back
 * to reloading the current style instead, which discards that kind of change too.
 *
 * `onSettled` fires once the entry is safe to reclaim. The fast path is synchronous.
 * The fallback isn't: reloading the *same* style URL diffs the existing Style object in
 * place, and Mapbox only fires "style.load" for a brand-new one, so this waits on "idle"
 * instead, which fires either way.
 */
function resetEntry(entry: LeaseEntry, onSettled: () => void) {
  entry.listeners.forEach(({ type, listener }) => entry.map.off(type as any, listener as any));
  entry.listeners = [];

  entry.map
    .getContainer()
    .querySelectorAll(".mapboxgl-marker")
    .forEach((marker) => marker.remove());

  // A prior claimer may have disabled these (e.g. a non-interactive preview card) -
  // hand back a fully interactive instance by default, same as a brand-new map; a new
  // consumer that wants it non-interactive disables it itself.
  INTERACTION_HANDLERS.forEach((handlerKey) => (entry.map[handlerKey] as any).enable());
  entry.map.getCanvas().style.removeProperty("cursor");

  entry.map.setPadding({ top: 0, right: 0, bottom: 0, left: 0 });
  entry.map.setPitch(0);
  entry.map.setBearing(0);
  // setTerrain throws if the style hasn't loaded yet - and if it hasn't, a consumer
  // couldn't have enabled terrain in the first place, so there's nothing to reset.
  if (entry.map.isStyleLoaded()) {
    entry.map.setTerrain(null);
  }

  if (entry.needsFullReset && entry.currentStyle) {
    entry.needsFullReset = false;
    entry.addedLayerIds.clear();
    entry.addedSourceIds.clear();
    entry.map.once("idle", onSettled);
    entry.map.setStyle(entry.currentStyle);
    return;
  }

  // Layers first - Mapbox refuses to remove a source that a layer still references.
  entry.addedLayerIds.forEach((id) => {
    if (entry.map.getLayer(id)) {
      entry.map.removeLayer(id);
    }
  });
  entry.addedLayerIds.clear();
  entry.addedSourceIds.forEach((id) => {
    if (entry.map.getSource(id)) {
      entry.map.removeSource(id);
    }
  });
  entry.addedSourceIds.clear();
  onSettled();
}

/**
 * Wraps entry.map in a Proxy that passes almost everything straight through to the real
 * map, bound to it rather than to the proxy - mapbox-gl reads/writes private instance
 * fields that only exist on the real map, so an unbound passthrough would fork that state.
 * A handful of methods are intercepted instead:
 * - on/off: tracked as a safety net, not a substitute for a consumer's own cleanup - a
 *   listener a consumer forgets to unregister still gets caught on release instead of
 *   leaking onto whoever claims this instance next. A reused, already-loaded instance also
 *   gets an immediate call for "load" (instead of registering - it only ever fires once,
 *   and already did) and "style.load" (registered too, since unlike "load" it can
 *   genuinely fire again later).
 * - setStyle: deduped and remembered so the next claim's "does this differ" check has an
 *   accurate value.
 * - addLayer/addSource/removeLayer/removeSource: tracked so release can strip exactly
 *   what a consumer added (see resetEntry) without touching anything else.
 * - setLayoutProperty/setPaintProperty/setFilter/setLayerZoomRange/setConfigProperty:
 *   flag entry.needsFullReset when the target wasn't added by this consumer (or, for a
 *   style config property, unconditionally - there's no "did I add this" to check), so
 *   release falls back to a full reload instead of leaking the mutation to the next
 *   claimer (see resetEntry).
 * - remove: released back to the pool instead of destroying the map.
 */
function createPooledMap(entry: LeaseEntry, release: () => void): mapboxgl.Map {
  return new Proxy(entry.map, {
    get(target, prop, receiver) {
      if (prop === "on") {
        return (type: string, listener: (...args: any[]) => void) => {
          // The real "load" event only fires once per instance and already did for a
          // reused one - fire immediately, unless a claim's own setStyle (see
          // tryClaimFreeEntry) has the style still mid-reload, in which case defer to a
          // one-time "style.load" instead.
          if (type === "load" && entry.hasLoaded) {
            if (!entry.styleReloading) {
              listener();
              return receiver;
            }
            const onceStyleLoad = (...args: any[]) => {
              target.off("style.load" as any, onceStyleLoad as any);
              entry.listeners = entry.listeners.filter(
                (record) => record.listener !== onceStyleLoad
              );
              entry.styleReloading = false;
              listener(...args);
            };
            target.on("style.load" as any, onceStyleLoad as any);
            entry.listeners.push({ type: "style.load", listener: onceStyleLoad });
            return receiver;
          }
          // Unlike "load", "style.load" can legitimately fire again later (e.g. a map-type
          // toggle's own setStyle call) - a reused instance's already-loaded style still
          // needs the immediate call a fresh instance's consumer would otherwise wait
          // forever for, but the real listener stays registered too, for that later one.
          if (type === "style.load" && target.isStyleLoaded()) {
            listener();
          }
          target.on(type as any, listener as any);
          entry.listeners.push({ type, listener });
          return receiver;
        };
      }
      if (prop === "off") {
        return (type: string, listener: (...args: any[]) => void) => {
          target.off(type as any, listener as any);
          entry.listeners = entry.listeners.filter(
            (record) => record.type !== type || record.listener !== listener
          );
          return receiver;
        };
      }
      if (prop === "setStyle") {
        return (style: string, ...rest: unknown[]) => {
          if (style === entry.currentStyle) {
            return receiver;
          }
          entry.currentStyle = style;
          return (target.setStyle as (...args: unknown[]) => unknown)(style, ...rest);
        };
      }
      if (prop === "addLayer") {
        return (layer: unknown, ...args: unknown[]) => {
          const result = (target.addLayer as (...a: unknown[]) => unknown)(layer, ...args);
          const id = (layer as { id?: string } | undefined)?.id;
          if (id) {
            entry.addedLayerIds.add(id);
          }
          return result;
        };
      }
      if (prop === "addSource") {
        return (id: string, source: unknown, ...args: unknown[]) => {
          const result = (target.addSource as (...a: unknown[]) => unknown)(id, source, ...args);
          entry.addedSourceIds.add(id);
          return result;
        };
      }
      if (prop === "removeLayer") {
        return (id: string) => {
          entry.addedLayerIds.delete(id);
          return target.removeLayer(id);
        };
      }
      if (prop === "removeSource") {
        return (id: string) => {
          entry.addedSourceIds.delete(id);
          return target.removeSource(id);
        };
      }
      if (
        prop === "setLayoutProperty" ||
        prop === "setPaintProperty" ||
        prop === "setFilter" ||
        prop === "setLayerZoomRange"
      ) {
        return (layerId: string, ...args: unknown[]) => {
          if (!entry.addedLayerIds.has(layerId)) {
            entry.needsFullReset = true;
          }
          return (target[prop] as (...a: unknown[]) => unknown)(layerId, ...args);
        };
      }
      if (prop === "setConfigProperty") {
        return (...args: unknown[]) => {
          entry.needsFullReset = true;
          return (target.setConfigProperty as (...a: unknown[]) => unknown)(...args);
        };
      }
      if (prop === "remove") {
        return () => release();
      }
      const value = Reflect.get(target, prop, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  }) as mapboxgl.Map;
}

export const SharedMapProvider = ({ children }: PropsWithChildren) => {
  const leasesRef = useRef<LeaseEntry[]>([]);
  /** The offscreen DOM node every free (unclaimed) instance's container gets parked in - see the JSX below. */
  const parkingLotRef = useRef<HTMLDivElement>(null);
  const mapboxglModulePromiseRef = useRef<Promise<typeof import("mapbox-gl")>>();

  const claimMap = useCallback(
    async (container: HTMLDivElement, options?: ClaimOptions): Promise<mapboxgl.Map> => {
      const owner = Symbol("sharedMapOwner");

      const releaseEntry = (entry: LeaseEntry) => () => {
        // Already released, or this owner never held this entry (e.g. a stale/duplicate
        // teardown call) - nothing to do.
        if (entry.owner !== owner || !parkingLotRef.current) {
          return;
        }
        entry.resettling = new Promise((resolve) => {
          resetEntry(entry, () => {
            entry.owner = undefined;
            entry.resettling = undefined;
            resolve();
          });
        });
        parkingLotRef.current.appendChild(entry.map.getContainer());
      };

      // Finds a free entry, reparents it into `container`, applies the requested style, and
      // hands it back wrapped in the same Proxy a brand-new instance gets - or undefined if
      // the pool has nothing free right now.
      const tryClaimFreeEntry = (): mapboxgl.Map | undefined => {
        const entry = leasesRef.current.find((e) => e.owner === undefined);
        if (!entry) {
          return undefined;
        }
        const mapContainer = entry.map.getContainer();
        if (mapContainer.parentElement !== container) {
          container.appendChild(mapContainer);
        }
        if (options?.style && options.style !== entry.currentStyle) {
          entry.currentStyle = options.style;
          entry.styleReloading = true;
          entry.map.once("style.load", () => {
            entry.styleReloading = false;
          });
          entry.map.setStyle(options.style);
        }
        entry.map.resize();
        // The container's final layout size isn't always settled in the same tick as the
        // reparent above (e.g. a drawer still animating open) - a follow-up resize on the
        // next frame catches that instead of leaving the map sized for its old home.
        requestAnimationFrame(() => entry.map.resize());

        entry.owner = owner;
        return createPooledMap(entry, releaseEntry(entry));
      };

      const claimed = tryClaimFreeEntry();
      if (claimed) {
        return claimed;
      }

      // Nothing free right now - if an entry is still finishing a release (its full
      // reset fallback hasn't settled yet), wait for that instead of paying for a whole
      // new Map and a fresh style/tile/sprite fetch.
      const resettlingEntry = leasesRef.current.find((entry) => entry.resettling);
      if (resettlingEntry) {
        await resettlingEntry.resettling;
        const claimedAfterSettle = tryClaimFreeEntry();
        if (claimedAfterSettle) {
          return claimedAfterSettle;
        }
      }

      mapboxglModulePromiseRef.current ??= import("mapbox-gl");
      const { default: Mapbox } = await mapboxglModulePromiseRef.current;
      // A concurrent claim may have freed an entry while this one awaited the import.
      const claimedAfterImport = tryClaimFreeEntry();
      if (claimedAfterImport) {
        return claimedAfterImport;
      }

      // Give the map its own dedicated internal div rather than the caller's container directly -
      // mapbox-gl treats whichever container it's constructed with as permanent, but a caller's
      // own container can get reparented (e.g. into the parking lot on release) while the caller
      // keeps the same ref, which would break a later claim's "is this already my container" check.
      const mapDiv = document.createElement("div");
      mapDiv.style.width = "100%";
      mapDiv.style.height = "100%";
      container.appendChild(mapDiv);
      const map = new Mapbox.Map({ container: mapDiv, style: options?.style });
      const entry: LeaseEntry = {
        map,
        owner,
        currentStyle: options?.style,
        hasLoaded: false,
        listeners: [],
        addedLayerIds: new Set(),
        addedSourceIds: new Set(),
        needsFullReset: false,
        styleReloading: false,
      };
      map.once("load", () => {
        entry.hasLoaded = true;
      });
      leasesRef.current.push(entry);
      return createPooledMap(entry, releaseEntry(entry));
    },
    []
  );

  return (
    <SharedMapContext.Provider value={{ claimMap }}>
      {children}
      {/* Parks every free instance's container here (and its live WebGL context), so
          they survive between claims instead of being torn out of the DOM along with
          whatever component last owned them. */}
      <div
        ref={parkingLotRef}
        style={{
          position: "fixed",
          top: -9999,
          left: -9999,
          width: 1,
          height: 1,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      />
    </SharedMapContext.Provider>
  );
};
